/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
const LOCAL_STORAGE_IS_DB2_KEY = 'MAM_Tenant_is_DB2';
const LOCAL_STORAGE_IS_BETA_PREVIEW_ENABLED_KEY = 'MAM_Beta_Preview_Enabled';
const LOCAL_STORAGE_IS_EMBEDDED = 'MAM_Embedded_Content';

// Set to and retrieve whether this tenant is db2
export const setLocalStorageIsDB2 = isDB2 => localStorage.setItem(LOCAL_STORAGE_IS_DB2_KEY, isDB2);

export const getLocalStorageIsDB2 = () => localStorage.getItem(LOCAL_STORAGE_IS_DB2_KEY);
/**
 * Use setWithExpiry in combination with getWithExpiry function to set local storage based on exiry time
 * @param {*} key key to be used in the local storage and its used while retrieval of the value
 * @param {*} value to be stored in the local storage
 * @param {*} expiryTime time to leave in milliseconds. Default expires after 1 hours
 */
export const setWithExpiry = (key, value, expiryTime = 1 * 60 * 60 * 1000) => {
  const now = new Date();

  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value,
    expiry: now.getTime() + expiryTime,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

/**
 * Use getWithExpiry in combination with setWithExpiry function to get the local storage based on exiry time
 * @param {*} key
 * @returns
 */
export const getWithExpiry = key => {
  const itemStr = localStorage.getItem(key);

  // if the item doesn't exist, return null
  if (!itemStr) {
    return null;
  }

  const item = JSON.parse(itemStr);
  const now = new Date();

  // compare the expiry time of the item with the current time
  if (now.getTime() > item.expiry) {
    // If the item is expired, delete the item from storage and return null
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
};

// Set to and retrieve if we should show beta preview content
export const setBetaPreviewEnabled = hasBetaPreview =>
  setWithExpiry(
    LOCAL_STORAGE_IS_BETA_PREVIEW_ENABLED_KEY,
    hasBetaPreview,
    24 * 60 * 60 * 1000 // expires after 24 hours
  );

export const hasBetaPreviewEnabled = () => {
  const hasBetaPreview = getWithExpiry(LOCAL_STORAGE_IS_BETA_PREVIEW_ENABLED_KEY);
  return (hasBetaPreview && hasBetaPreview === 'true') || false;
};

// Set to and retrieve whether this resource should be embedded into another frame
export const setLocalStorageIsEmbedded = isEmbedded =>
  localStorage.setItem(LOCAL_STORAGE_IS_EMBEDDED, isEmbedded);

export const getLocalStorageIsEmbedded = () => {
  const isEmbedded = localStorage.getItem(LOCAL_STORAGE_IS_EMBEDDED);
  return (isEmbedded && isEmbedded === 'true') || false;
};
